import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import { InstallationsContext } from 'src/contexts/InstallationsContextProvider';
import FileUploadButton from 'src/components/FileUploadButton';
import DocumentList from 'src/components/DocumentList';

// The end-customer / grid / system fields the installer fills at step 1 of the
// Installation Checklist. These write directly to the same Installation columns
// the Information tab uses (single source of truth) via the narrow
// UpdateInstallationChecklistDetails endpoint.
interface Props {
  installation: I_Installation;
  disabled: boolean; // signed & not admin → read-only
  isAdmin: boolean;
  onSaved: (updated: I_Installation) => void;
}

type DetailValues = {
  street: string;
  postCode: string;
  city: string;
  canton: string;
  country: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  networkProvider: string;
  externalEms: string;
  kilowattPeak: string;
  couplingType: string;
  backupLoad: string;
};

const EMS_KNOWN = ['No', 'Solar Manager', 'Smart Fox', 'Loxone'];
// Full kWp (up to 5 whole digits, optional 1–2 decimals). Empty is allowed while
// editing but blocks signing (kWp is required).
const KWP_FULL = /^\d{1,5}(\.\d{1,2})?$/;
// Lenient guard for keystrokes (permits partial input like "12" or "12.").
const KWP_PARTIAL = /^\d{0,5}(\.\d{0,2})?$/;

function toValues(i: I_Installation): DetailValues {
  return {
    street: i.street ?? '',
    postCode: i.postCode ?? '',
    city: i.city ?? '',
    canton: i.canton ?? '',
    country: i.country ?? '',
    firstName: i.firstName ?? '',
    lastName: i.lastName ?? '',
    email: i.email ?? '',
    phone: i.phone ?? '',
    networkProvider: i.networkProvider ?? '',
    externalEms: i.externalEms ?? '',
    kilowattPeak: i.kilowattPeak ?? '',
    couplingType: i.couplingType ?? '',
    backupLoad: i.backupLoad ?? ''
  };
}

function InstallationDetailsSection({ installation, disabled, isAdmin, onSaved }: Props) {
  const intl = useIntl();
  const context = useContext(InstallationsContext) as any;
  const [values, setValues] = useState<DetailValues>(() => toValues(installation));
  const [networkProviders, setNetworkProviders] = useState<string[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docsRefreshKey, setDocsRefreshKey] = useState(0);
  const [toast, setToast] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: ''
  });

  // Re-sync local edits when switching to a different installation.
  useEffect(() => {
    setValues(toValues(installation));
  }, [installation.id]);

  useEffect(() => {
    setLoadingProviders(true);
    axiosConfig
      .get('/GetNetworkProviders')
      .then((res) => setNetworkProviders(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoadingProviders(false));
  }, []);

  const setField = (name: keyof DetailValues, value: string) =>
    setValues((v) => ({ ...v, [name]: value }));

  const handleKwpChange = (val: string) => {
    if (val === '' || KWP_PARTIAL.test(val)) setField('kilowattPeak', val);
  };

  const kwpInvalid = values.kilowattPeak !== '' && !KWP_FULL.test(values.kilowattPeak);

  const emsSelectValue = !values.externalEms
    ? ''
    : EMS_KNOWN.includes(values.externalEms)
    ? values.externalEms
    : 'Other';

  const handleSave = async () => {
    if (kwpInvalid) return;
    setSaving(true);
    try {
      await axiosConfig.put('/UpdateInstallationChecklistDetails', null, {
        params: { installationId: installation.id, ...values }
      });
      // Merge edits into the existing installation locally — the endpoint returns
      // no body (it would otherwise re-expose S3 secrets to partners).
      onSaved({
        ...installation,
        ...values,
        couplingType: values.couplingType || null,
        backupLoad: values.backupLoad || null
      });
      // Refresh the shared context so the Information tab reflects these values.
      if (context?.fetchAllInstallations) context.fetchAllInstallations(installation.product);
      if (context?.fetchAllFoldersAndInstallations)
        context.fetchAllFoldersAndInstallations(installation.product);
      setToast({
        open: true,
        severity: 'success',
        message: intl.formatMessage({ id: 'installationDetailsSaved', defaultMessage: 'Installation details saved' })
      });
    } catch {
      setToast({
        open: true,
        severity: 'error',
        message: intl.formatMessage({
          id: 'installationDetailsSaveFailed',
          defaultMessage: 'Could not save installation details'
        })
      });
    } finally {
      setSaving(false);
    }
  };

  const optionalSuffix = useMemo(
    () => ` (${intl.formatMessage({ id: 'optional', defaultMessage: 'optional' })})`,
    [intl]
  );

  const text = (
    name: keyof DetailValues,
    labelKey: string,
    labelDefault: string,
    opts?: { required?: boolean; optional?: boolean; helper?: string }
  ) => (
    <TextField
      label={
        <>
          <FormattedMessage id={labelKey} defaultMessage={labelDefault} />
          {opts?.optional ? optionalSuffix : ''}
        </>
      }
      value={values[name]}
      onChange={(e) => setField(name, e.target.value)}
      variant="outlined"
      fullWidth
      required={opts?.required}
      helperText={opts?.helper}
      inputProps={{ readOnly: disabled }}
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        1.&nbsp;<FormattedMessage id="installationDetails" defaultMessage="Installation details" />
      </Typography>

      {/* Phones: full-width single column (fields are already fullWidth). Desktop
          keeps the 520px cap. */}
      <Box sx={{ maxWidth: { xs: '100%', md: 520 } }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {text('street', 'street', 'Street', {
            required: true,
            helper: intl.formatMessage({ id: 'streetAndNumber', defaultMessage: 'Street and house number' })
          })}
          {text('postCode', 'postCode', 'Postcode', { required: true })}
          {text('city', 'city', 'City', { required: true })}
          {text('canton', 'canton', 'Canton', { required: true })}
          {text('country', 'country', 'Country', { required: true })}

          {text('firstName', 'firstName', 'First name', { optional: true })}
          {text('lastName', 'lastName', 'Last name', { optional: true })}
          {text('email', 'emailAddress', 'Email Address', { required: true })}
          {text('phone', 'phone', 'Phone number', { optional: true })}

          <Autocomplete
            freeSolo
            options={networkProviders}
            value={values.networkProvider}
            onChange={(_e, val) => setField('networkProvider', (val as string) || '')}
            onInputChange={(_e, val) => setField('networkProvider', val || '')}
            disabled={disabled}
            loading={loadingProviders}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label={<FormattedMessage id="networkProvider" defaultMessage="Grid Provider" />}
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingProviders ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />

          <TextField
            label={<FormattedMessage id="kilowattPeak" defaultMessage="kWp (PV system)" />}
            value={values.kilowattPeak}
            onChange={(e) => handleKwpChange(e.target.value)}
            variant="outlined"
            fullWidth
            required
            error={kwpInvalid}
            helperText={kwpInvalid ? 'max. 5 digits (e.g. 9.6)' : undefined}
            inputProps={{ readOnly: disabled, inputMode: 'decimal' }}
            InputProps={{ endAdornment: <InputAdornment position="end">kWp</InputAdornment> }}
          />

          <FormControl fullWidth required>
            <InputLabel shrink sx={{ backgroundColor: 'white', px: 0.5 }}>
              <FormattedMessage id="externalEms" defaultMessage="External EMS" />
            </InputLabel>
            <Select
              value={emsSelectValue}
              onChange={(e) => setField('externalEms', e.target.value as string)}
              inputProps={{ readOnly: disabled }}
              displayEmpty
              notched
            >
              <MenuItem value="">
                <em><FormattedMessage id="notSet" defaultMessage="Not set" /></em>
              </MenuItem>
              <MenuItem value="No"><FormattedMessage id="emsNo" defaultMessage="No" /></MenuItem>
              <MenuItem value="Solar Manager">Solar Manager</MenuItem>
              <MenuItem value="Smart Fox">Smart Fox</MenuItem>
              <MenuItem value="Loxone">Loxone</MenuItem>
              <MenuItem value="Other"><FormattedMessage id="emsOther" defaultMessage="Other" /></MenuItem>
            </Select>
          </FormControl>

          {values.externalEms && !EMS_KNOWN.includes(values.externalEms) && (
            <TextField
              label={<FormattedMessage id="externalEmsOther" defaultMessage="External EMS (specify)" />}
              value={values.externalEms === 'Other' ? '' : values.externalEms}
              onChange={(e) => setField('externalEms', e.target.value)}
              variant="outlined"
              fullWidth
              inputProps={{ readOnly: disabled }}
            />
          )}

          <FormControl fullWidth required>
            <InputLabel shrink sx={{ backgroundColor: 'white', px: 0.5 }}>
              <FormattedMessage id="couplingType" defaultMessage="AC/DC Coupling" />
            </InputLabel>
            <Select
              value={values.couplingType}
              onChange={(e) => setField('couplingType', e.target.value as string)}
              inputProps={{ readOnly: disabled }}
              displayEmpty
              notched
            >
              <MenuItem value="">
                <em><FormattedMessage id="notSet" defaultMessage="Not set" /></em>
              </MenuItem>
              <MenuItem value="AC"><FormattedMessage id="couplingAC" defaultMessage="AC-coupled" /></MenuItem>
              <MenuItem value="DC"><FormattedMessage id="couplingDC" defaultMessage="DC-coupled" /></MenuItem>
              <MenuItem value="Mixed"><FormattedMessage id="couplingMixed" defaultMessage="Mixed" /></MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel shrink sx={{ backgroundColor: 'white', px: 0.5 }}>
              <FormattedMessage id="backupLoad" defaultMessage="Backup Load" />
            </InputLabel>
            <Select
              value={values.backupLoad}
              onChange={(e) => setField('backupLoad', e.target.value as string)}
              inputProps={{ readOnly: disabled }}
              displayEmpty
              notched
            >
              <MenuItem value="">
                <em><FormattedMessage id="notSet" defaultMessage="Not set" /></em>
              </MenuItem>
              <MenuItem value="Whole"><FormattedMessage id="backupWhole" defaultMessage="Whole house" /></MenuItem>
              <MenuItem value="Partial"><FormattedMessage id="backupPartial" defaultMessage="Partial" /></MenuItem>
              <MenuItem value="None"><FormattedMessage id="backupNone" defaultMessage="Not used" /></MenuItem>
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              <FormattedMessage id="modulePlan" defaultMessage="PV module plan" />
              {optionalSuffix}
            </Typography>
            <FileUploadButton
              scope={1}
              installationId={installation.id}
              subtaskKey="modulePlan"
              disabled={disabled}
              onUploaded={() => setDocsRefreshKey((k) => k + 1)}
            />
            <Box sx={{ mt: 1 }}>
              <DocumentList
                installationId={installation.id}
                subtaskKey="modulePlan"
                refreshKey={docsRefreshKey}
                canDelete={isAdmin}
              />
            </Box>
          </Box>

          {!disabled && (
            <Box>
              <Button variant="contained" color="primary" disabled={saving || kwpInvalid} onClick={handleSave}>
                <FormattedMessage id="saveDetails" defaultMessage="Save details" />
              </Button>
            </Box>
          )}
        </Stack>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default InstallationDetailsSection;
